import React, { Suspense, useMemo, useRef } from 'react'
import {
    BrowserRouter as Router,
    Switch,
    Route,
} from 'react-router-dom'

import { Home, ProductListing, ProductCreate, AppsListing, AppCreate, Login, Error404, Nav, OrderListing, AppEdit, ProductEdit } from './components'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import configs from './configs'
import { ApolloProvider } from '@apollo/client'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { MOCK_ENABLED } from './mocks'
import { MockAuth0Provider } from './mocks/auth'
import { createMockApolloClient } from './mocks/apollo'

function AuthorizedApolloProvider({ children }: { children: React.ReactNode }): JSX.Element {
    const auth0 = useAuth0()
    // Keep the latest Auth0 context in a ref so the token getter is always
    // current, while building the Apollo client exactly once (a fresh client
    // on every render would discard the normalized cache each time).
    const auth0Ref = useRef(auth0)
    auth0Ref.current = auth0
    const client = useMemo(
        () => configs.createApolloClient(() => auth0Ref.current.getAccessTokenSilently()),
        []
    )

    return <ApolloProvider client={client}>{children}</ApolloProvider>
}

type RouteEntry = {
    path: string;
    view: React.FunctionComponent<Record<string, unknown>>,
    exact?: boolean
}

const createProtected = <P extends Record<string, unknown>>(
    Component: React.ComponentType<P>
): React.FC<P> => {
    return function WithAuthenticationRequired(props: P): JSX.Element {
        const { isAuthenticated } = useAuth0()
  
        return isAuthenticated ? (
            <>
                <Nav></Nav>
                <Component {...props} />
            </>
        ) : (<Login />)
    }
}

const routes: Array<RouteEntry> = [
    { path: '/apps', view: AppsListing, exact: true },
    { path: '/apps/create', view: AppCreate, exact: true },
    { path: '/apps/edit/:id', view: AppEdit, exact: true },
    { path: '/products', view: ProductListing, exact: true },
    { path: '/products/create', view: ProductCreate, exact: true },
    { path: '/products/edit/:id', view: ProductEdit, exact: true },
    { path: '/orders', view: OrderListing, exact: true },
    { path: '/', view: Home, exact: true },
    { path: '/', view: Error404, exact: false },
]

function AppRoutes(): JSX.Element {
    return (
        <main className='App'>
            <Router>
                <Switch>
                    {routes.map((route, index) => (
                        <Route
                            key={index}
                            path={route.path}
                            exact={route.exact}
                            render={() => {
                                const Protected = createProtected(route.view)
                                return (
                                    <>
                                        <Protected />
                                        <ToastContainer></ToastContainer>
                                    </>)
                            }}
                        />
                    ))}
                </Switch>
            </Router>
        </main>
    )
}

// Built once, only in mock mode (createMockApolloClient is never called in prod).
const mockApolloClient = MOCK_ENABLED ? createMockApolloClient() : null

function App(): JSX.Element {
    if (MOCK_ENABLED && mockApolloClient) {
        return (
            <Suspense fallback='loading'>
                <MockAuth0Provider>
                    <ApolloProvider client={mockApolloClient}>
                        <AppRoutes />
                    </ApolloProvider>
                </MockAuth0Provider>
            </Suspense>
        )
    }

    return (
        <Suspense fallback='loading'>
            <Auth0Provider
                domain={configs.auth0_domain}
                clientId={configs.auth0_client_id}
                redirectUri={window.location.origin}
                useRefreshTokens={true}
                cacheLocation='localstorage'
                audience='dashboard'
                scope='dashboard:manage applications:manage products:manage orders:manage'
            >
                <AuthorizedApolloProvider>
                    <AppRoutes />
                </AuthorizedApolloProvider>
            </Auth0Provider>
        </Suspense>
    )
}

export default App
